/**
 * The safe primitives every block renderer is built from.
 *
 * **The single most important rule in this package:** customer content is rendered as ESCAPED TEXT.
 * `SiteText` puts the string in a text node and lets the runtime escape it. There is no `Rich`, no
 * Markdown pass and no `dangerouslySetInnerHTML` anywhere in `@ksa/render`, and there must not be —
 * everything in a builder document is written by a customer or by a model, and the publish path
 * emits static HTML *outside* React, which carries none of react-dom's protections.
 *
 * The second rule: **this file decides what a URL is allowed to be**, using `isHttpUrl` imported
 * from `@ksa/model` rather than a copy. A document can reach a renderer unvalidated — a draft
 * restored from `localStorage` is a raw `JSON.parse`; an AI patch is another such path — so the
 * check belongs where the `href` is emitted, not only where a document is checked.
 */

import { createContext, useContext, type ReactNode } from 'react'
import {
  isHttpUrl,
  resolve,
  hasText as modelHasText,
  type ImageRef,
  type Lang,
  type Link,
  type Page,
  type Site,
  type Text,
} from '@ksa/model'

export type RenderMode =
  /** Inside the editor. Internal links must not navigate the editor's router away, and a
   *  placeholder image is a legitimate thing to draw. */
  | 'preview'
  /** Publishing to static HTML. Links are real, images resolve to their public URLs. */
  | 'publish'

type RenderCtx = {
  site: Site
  lang: Lang
  mode: RenderMode
  /**
   * How an image key becomes a URL.
   *
   * ⚠️ INJECTED RATHER THAN COMPUTED, because the two callers genuinely differ and neither can guess
   * the other's answer. In the editor an image is served from the API behind a session; on a
   * published page it is a public object on the site's own origin. A renderer that hard-coded either
   * would be wrong in the other, and the way that presents is broken images on live customer sites.
   */
  mediaUrl: (key: string) => string
  /**
   * The page being rendered.
   *
   * ⚠️ IT IS HERE FOR THE LANGUAGE PILL, WHICH HAS TO LINK TO *THIS* PAGE IN THE OTHER LANGUAGE.
   * Sending every visitor to the home page instead is the version of a language switcher that
   * loses somebody's place — they were reading the treatments page and now they are not.
   */
  page: Page
  /**
   * Where the BUILDER lives — not where this site lives.
   *
   * ⚠️ IT IS THE ONE PLACE A PUBLISHED CUSTOMER PAGE POINTS BACK AT US, and it exists for the report
   * link in the footer. Injected rather than hard-coded for the usual reason plus one specific one:
   * a staging build that links its report form at the production domain sends real reports about
   * fake sites to the queue that handles real ones.
   */
  builderOrigin: string
}

/** Where the builder lives when nobody said. One declaration; `apps/api` passes its own `BASE_URL`. */
export const DEFAULT_BUILDER_ORIGIN = 'https://ksa-web.app'

const Ctx = createContext<RenderCtx | null>(null)

export function RenderProvider({
  site,
  lang,
  mode,
  mediaUrl,
  page,
  builderOrigin = DEFAULT_BUILDER_ORIGIN,
  children,
}: Omit<RenderCtx, 'builderOrigin'> & { builderOrigin?: string; children: ReactNode }) {
  return <Ctx.Provider value={{ site, lang, mode, mediaUrl, page, builderOrigin }}>{children}</Ctx.Provider>
}

export function useRender(): RenderCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('@ksa/render: a block was rendered outside <RenderProvider>')
  return ctx
}

/** A localised string, escaped. */
export function SiteText({ value }: { value: Text | undefined }) {
  const { lang } = useRender()
  if (!value) return null
  return <>{resolve(value, lang)}</>
}

export const hasText = modelHasText

/**
 * Prose split into paragraphs on blank lines.
 *
 * ⚠️ THIS IS THE WHOLE OF THE "RICH TEXT" STORY, DELIBERATELY. Blank line means new paragraph;
 * nothing else is interpreted. The moment this understands `**bold**` it is a parser turning
 * customer input into markup, on a page we publish under our own certificate — and every such parser
 * has had an escape found in it. If formatting is ever wanted, it is a structured field in the block
 * registry, not a syntax inside a string.
 */
export function SiteProse({ value, className }: { value: Text | undefined; className?: string }) {
  const { lang } = useRender()
  if (!value) return null
  const paragraphs = resolve(value, lang)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (paragraphs.length === 0) return null
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        // Single newlines inside a paragraph are preserved as line breaks, which is what somebody
        // typing an address into a text box expects.
        <p key={i} style={{ marginBlockStart: i === 0 ? 0 : '1em', whiteSpace: 'pre-line' }}>
          {p}
        </p>
      ))}
    </div>
  )
}

/**
 * Resolve a `Link` to an href for the CUSTOMER's site.
 *
 * Internal links resolve to the target page's SLUG — never its id, and never its (banned) Arabic
 * title. The home page resolves to `/` rather than to `/home`, because a published site's root is
 * the page a visitor arrives at and `/home` beside `/` is two URLs for one page.
 */
export function hrefFor(link: Link | undefined, site: Site, lang: Lang): string | undefined {
  if (!link || link.kind === 'none') return undefined
  if (link.kind === 'external') return isHttpUrl(link.href) ? link.href : undefined

  const index = site.pages.findIndex((p) => p.id === link.pageId)
  const page = site.pages[index]
  if (!page) return undefined

  // The published layout is `/<lang>/<slug>` with the default language also served at the root —
  // see `html.tsx`. Keeping that shape in ONE function is what stops the navbar and the footer
  // disagreeing about where a page lives.
  const prefix = lang === site.defaultLang ? '' : `/${lang}`
  const path = index === 0 ? `${prefix}/` : `${prefix}/${page.slug}`
  return `${path}${link.anchor ? `#${link.anchor}` : ''}`
}

/** An anchor when the link has a destination, plain text otherwise. In preview it does not navigate,
 *  so clicking around a preview never yanks the editor's router out from under the customer. */
export function SiteLink({
  link,
  className,
  children,
}: {
  link: Link | undefined
  className?: string
  children: ReactNode
}) {
  const { site, lang, mode } = useRender()
  const href = hrefFor(link, site, lang)
  if (!href) return <span className={className}>{children}</span>

  const external = link?.kind === 'external'
  return (
    <a
      className={className}
      href={href}
      target={external ? '_blank' : undefined}
      // `nofollow` because these are customer-supplied outbound links and we are not vouching for
      // them; `noopener` because `target="_blank"` without it hands the opened page a handle on
      // ours.
      rel={external ? 'noopener noreferrer nofollow' : undefined}
      onClick={mode === 'preview' ? (e) => e.preventDefault() : undefined}
    >
      {children}
    </a>
  )
}

const ASPECT: Record<string, string> = {
  landscape: 's-ar-landscape',
  portrait: 's-ar-portrait',
  square: 's-ar-square',
}

/**
 * An image, or the honest absence of one.
 *
 * A `placeholder` renders as an empty dashed box that keeps the aspect ratio a picture will take —
 * not a missing-image error. It is the layout telling the truth about a space that is not filled yet.
 */
export function SiteImage({ image, className }: { image: ImageRef | undefined; className?: string }) {
  const { lang, mediaUrl, mode } = useRender()
  if (!image) return null
  const aspect = ASPECT[image.orientation] ?? ASPECT.landscape!

  if (image.status === 'placeholder') {
    /**
     * ⚠️ **A PUBLISHED PAGE DRAWS NOTHING AT ALL, AND THAT IS WHAT `RenderMode` HAS ALWAYS SAID** —
     * "a placeholder image is a legitimate thing to draw" is written on the `preview` arm of that
     * type and on no other. It was drawn in both, so a real business's live homepage carried a large
     * empty dashed rectangle where a photograph should be. The publish checklist warns that pictures
     * are missing and does not block, which is right; what it must not do is turn "unfinished" into
     * "broken".
     *
     * ⚠️ AND THIS IS THE ONE PLACE THE PREVIEW IS DELIBERATELY NOT WHAT PUBLISHES (§2.2). The
     * divergence is the editor showing MORE than a visitor does — the slot, so the customer can see
     * there is one and fill it — which is the same argument that lets `editor.css` paint a blurred
     * stand-in and a stamp over this box (§5.11). A visitor is shown the page as it stands.
     *
     * > ⚠️ **IT USED TO PRINT THE DESCRIPTION THE MODEL WROTE**, back when "describe it now, generate
     * > it later" was the plan. On-demand generation was dropped, so an empty slot is an empty slot.
     */
    if (mode !== 'preview') return null
    return <div className={`s-placeholder ${aspect} ${className ?? ''}`} aria-hidden="true" />
  }

  return (
    <div className={`s-media ${aspect} ${className ?? ''}`}>
      <img
        src={mediaUrl(image.key)}
        // ⚠️ `alt` IS ALWAYS PRESENT, and an empty string is a MEANINGFUL value rather than a
        // missing one: it tells a screen reader the image is decorative and to skip it. Omitting the
        // attribute makes the reader announce the filename instead, which on a published site is a
        // visitor hearing "media slash site underscore k3f9 slash hero dot jpg".
        alt={resolve(image.alt, lang)}
        loading="lazy"
        decoding="async"
      />
    </div>
  )
}

/** A phone number, an email address or a URL — an identifier rather than prose. Isolated so the
 *  bidirectional algorithm cannot rearrange it inside an Arabic sentence. */
export function Ltr({ children }: { children: ReactNode }) {
  return <span className="s-ltr">{children}</span>
}
